import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

/**
 * Laat een pagina zijn eigen laatste breadcrumb-label publiceren aan de
 * layout-header.
 *
 * Nodig omdat een detailroute er zo uitziet:
 *   /cms/purchase-orders/all/8Kd3fZq1PmR2
 * Een padgebaseerde breadcrumb maakt daar "all / 8Kd3fZq1PmR2" van, terwijl je
 * het recordnummer wil zien (IO-20260817-73). Dat nummer kent alleen de pagina
 * zelf, die het uit Firestore heeft geladen.
 *
 * De gedeelde `PageHeader` uit shared-components/purchase publiceert zijn titel
 * hier automatisch, dus geen enkele inkooppagina hoeft hiervoor aangepast te
 * worden.
 *
 * Bewust een context en geen redux-veld: de store wordt naar localStorage
 * gepersisteerd (`save()`/`load()` in redux/store), dus een kruimellabel zou
 * daar meegeschreven en bij een verse pageload gerehydrateerd worden — je zou
 * kort het recordnummer van de vorige sessie in de header zien staan.
 */
const PageCrumbContext = createContext(null);

/** Het huidige label, of null. Gooit niet als er geen provider is. */
export const usePageCrumbValue = () => useContext(PageCrumbContext)?.crumb ?? null;

/**
 * Setter voor het label. Gooit niet zonder provider, zodat de gedeelde
 * PageHeader ook buiten de app-layout blijft werken (bijv. in een modal of test).
 */
const NOOP = () => {};
export const useSetPageCrumb = () => useContext(PageCrumbContext)?.setCrumb ?? NOOP;

export const PageCrumbProvider = ({ children }) => {
  const [crumb, setCrumb] = useState(null);
  const value = useMemo(() => ({ crumb, setCrumb }), [crumb]);
  return <PageCrumbContext.Provider value={value}>{children}</PageCrumbContext.Provider>;
};

/**
 * Publiceert `label` zolang de component gemonteerd is en ruimt het daarna op.
 * Los exporteerbaar zodat een pagina die de gedeelde PageHeader niet gebruikt
 * alsnog een kruimel kan zetten.
 */
export const usePublishPageCrumb = (label) => {
  const setCrumb = useSetPageCrumb();
  useEffect(() => {
    setCrumb(label ?? null);
    return () => setCrumb(null);
  }, [label, setCrumb]);
};

export default PageCrumbContext;
